import React, { useEffect, useState } from 'react';
import { RefreshCw, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ExternalLink, Link2, Calculator, Image as ImageIcon, Package, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { mensajeErrorApi } from '../apiErrors';
import SeleccionarClienteInput from './SeleccionarClienteInput';
import CotizacionCompraAsistidaImagenModal from './CotizacionCompraAsistidaImagenModal';
import PaqueteFormModal from './PaqueteFormModal';

const ESTADO_LABEL = {
  nueva: 'Nueva',
  cotizando: 'Cotizando',
  cotizacion_enviada: 'Cotización enviada',
  confirmada: 'Confirmada',
  rechazada: 'Rechazada',
  sin_respuesta: 'Sin respuesta',
};

const ESTADO_COLORS = {
  nueva: { bg: '#E3F2FD', color: '#1565C0' },
  cotizando: { bg: '#FFF3E0', color: '#E65100' },
  cotizacion_enviada: { bg: '#FFF9C4', color: '#F57F17' },
  confirmada: { bg: '#E8F5E9', color: '#2E7D32' },
  rechazada: { bg: '#FFEBEE', color: '#C62828' },
  sin_respuesta: { bg: '#FAFAFA', color: '#9E9E9E' },
};

const FORMA_PAGO_LABEL = { transferencia: 'Transferencia', payphone: 'Payphone (tarjeta)' };

function formatFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-EC', {
    day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

export default function CompraAsistidaPanel() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [pagina, setPagina] = useState(1);
  const [porPagina] = useState(20);
  const [expandido, setExpandido] = useState(null);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [notasTemp, setNotasTemp] = useState({});
  const [vinculando, setVinculando] = useState(null);
  const [preciosTemp, setPreciosTemp] = useState({}); // { itemId: { precio_unitario_tienda, peso_lb_estimado } }
  const [cotizando, setCotizando] = useState(null);
  const [mostrarImagen, setMostrarImagen] = useState(null); // id de la solicitud
  const [creandoPaquete, setCreandoPaquete] = useState(null); // id de la solicitud

  const cargar = () => {
    setCargando(true);
    setSeleccionados(new Set());
    const params = { ordering: '-creado' };
    if (filtro) params.search = filtro;
    if (filtroEstado) params.estado = filtroEstado;
    api.get('/compra-asistida/', { params })
      .then((res) => setSolicitudes(res.data.results || res.data))
      .catch(() => toast.error('No se pudieron cargar las solicitudes.'))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    const timer = setTimeout(() => setFiltro(busqueda.trim()), 350);
    return () => clearTimeout(timer);
  }, [busqueda]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [filtro, filtroEstado]);
  useEffect(() => setPagina(1), [filtro, filtroEstado]);

  const totalPaginas = Math.max(1, Math.ceil(solicitudes.length / porPagina));
  const paginaActual = Math.min(pagina, totalPaginas);
  const paginadas = solicitudes.slice((paginaActual - 1) * porPagina, paginaActual * porPagina);

  const toggleSeleccion = (id) => setSeleccionados((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleTodosPagina = () => {
    const ids = paginadas.map((s) => s.id);
    const todos = ids.length > 0 && ids.every((id) => seleccionados.has(id));
    setSeleccionados((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (todos ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const eliminarSeleccionados = async () => {
    const ids = Array.from(seleccionados);
    const conPago = solicitudes.filter((s) => seleccionados.has(s.id) && s.pagado).length;
    const aviso = conPago > 0 ? ` OJO: ${conPago} tiene un pago registrado y ese registro se perderá.` : '';
    if (!window.confirm(`¿Eliminar ${ids.length} solicitud(es)? No se puede deshacer.${aviso}`)) return;
    try {
      await api.post('/compra-asistida/eliminar-lote/', { ids });
      toast.success(`${ids.length} solicitud(es) eliminada(s).`);
      cargar();
    } catch (err) {
      toast.error(mensajeErrorApi(err, 'No se pudieron eliminar.'));
    }
  };

  const resumen = {
    total: solicitudes.length,
    nuevas: solicitudes.filter((s) => s.estado === 'nueva').length,
    cotizando: solicitudes.filter((s) => s.estado === 'cotizando').length,
    confirmadas: solicitudes.filter((s) => s.estado === 'confirmada').length,
  };

  const cambiarEstado = async (id, nuevoEstado) => {
    try {
      const res = await api.patch(`/compra-asistida/${id}/`, { estado: nuevoEstado });
      setSolicitudes((prev) => prev.map((s) => (s.id === id ? res.data : s)));
      toast.success('Estado actualizado.');
    } catch {
      toast.error('No se pudo actualizar el estado.');
    }
  };

  const guardarNotas = async (id) => {
    try {
      const res = await api.patch(`/compra-asistida/${id}/`, { notas_internas: notasTemp[id] ?? '' });
      setSolicitudes((prev) => prev.map((s) => (s.id === id ? res.data : s)));
      toast.success('Notas guardadas.');
    } catch {
      toast.error('No se pudieron guardar las notas.');
    }
  };

  const vincularCliente = async (id, cliente) => {
    try {
      const res = await api.post(`/compra-asistida/${id}/vincular_cliente/`, { cliente_id: cliente.id });
      setSolicitudes((prev) => prev.map((s) => (s.id === id ? res.data : s)));
      toast.success('Cliente vinculado.');
    } catch {
      toast.error('No se pudo vincular el cliente.');
    } finally {
      setVinculando(null);
    }
  };

  const toggleExpandir = (s) => {
    if (expandido === s.id) {
      setExpandido(null);
    } else {
      setExpandido(s.id);
      setNotasTemp((prev) => ({ ...prev, [s.id]: prev[s.id] ?? (s.notas_internas || '') }));
      setPreciosTemp((prev) => {
        const next = { ...prev };
        (s.items || []).forEach((it) => {
          if (!next[it.id]) {
            next[it.id] = {
              precio_unitario_tienda: it.precio_unitario_tienda ?? '',
              peso_lb_estimado: it.peso_lb_estimado ?? '',
            };
          }
        });
        return next;
      });
    }
  };

  const actualizarPrecioTemp = (itemId, campo, valor) => {
    setPreciosTemp((prev) => ({ ...prev, [itemId]: { ...prev[itemId], [campo]: valor } }));
  };

  const cotizar = async (s) => {
    setCotizando(s.id);
    try {
      const items = (s.items || []).map((it) => ({
        id: it.id,
        precio_unitario_tienda: preciosTemp[it.id]?.precio_unitario_tienda || null,
        peso_lb_estimado: preciosTemp[it.id]?.peso_lb_estimado || null,
      }));
      const res = await api.post(`/compra-asistida/${s.id}/cotizar/`, { items });
      setSolicitudes((prev) => prev.map((x) => (x.id === s.id ? res.data : x)));
      toast.success('Cotización calculada.');
    } catch {
      toast.error('No se pudo calcular la cotización.');
    } finally {
      setCotizando(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
        <div className="card" style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
          <div className="text-xs text-secondary">Total</div>
          <div style={{ fontSize: '24px', fontWeight: 700 }}>{resumen.total}</div>
        </div>
        <div className="card" style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
          <div className="text-xs text-secondary">Nuevas</div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#1565C0' }}>{resumen.nuevas}</div>
        </div>
        <div className="card" style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
          <div className="text-xs text-secondary">Cotizando</div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#E65100' }}>{resumen.cotizando}</div>
        </div>
        <div className="card" style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
          <div className="text-xs text-secondary">Confirmadas</div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#2E7D32' }}>{resumen.confirmadas}</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
        <h2 style={{ marginBottom: 0 }}>Compra Asistida</h2>
        <div className="toolbar">
          <div className="toolbar__buscar">
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
            <input
              type="text" placeholder="Buscar nombre o correo..."
              value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              style={{ minWidth: '200px', paddingLeft: '32px' }}
            />
          </div>
          <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} style={{ minWidth: '160px' }}>
            <option value="">Todos los estados</option>
            {Object.entries(ESTADO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {seleccionados.size > 0 && (
            <button className="btn-ghost btn-sm" onClick={eliminarSeleccionados} style={{ color: '#D32F2F', whiteSpace: 'nowrap' }}>
              <Trash2 size={14} /> Eliminar ({seleccionados.size})
            </button>
          )}
          <button className="btn-secondary" onClick={cargar} style={{ whiteSpace: 'nowrap' }}>
            <RefreshCw size={14} /> Actualizar
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th style={{ width: '36px' }}>
                <input
                  type="checkbox"
                  aria-label="Seleccionar todas"
                  checked={paginadas.length > 0 && paginadas.every((s) => seleccionados.has(s.id))}
                  onChange={toggleTodosPagina}
                />
              </th>
              <th style={{ width: '36px' }}></th>
              <th>Fecha</th>
              <th>Nombre</th>
              <th>Email / WhatsApp</th>
              <th>Artículos</th>
              <th>Presupuesto</th>
              <th>Forma de pago</th>
              <th>Cliente</th>
              <th>A pagar ahora</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={11} className="text-secondary">Cargando...</td></tr>
            ) : solicitudes.length === 0 ? (
              <tr><td colSpan={11} className="text-secondary">No hay solicitudes registradas.</td></tr>
            ) : paginadas.map((s) => {
              const ec = ESTADO_COLORS[s.estado] || ESTADO_COLORS.nueva;
              const isOpen = expandido === s.id;
              return (
                <React.Fragment key={s.id}>
                  <tr style={{ cursor: 'pointer', background: isOpen ? 'var(--color-bg-secondary)' : undefined }} onClick={() => toggleExpandir(s)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={seleccionados.has(s.id)} onChange={() => toggleSeleccion(s.id)} />
                    </td>
                    <td>{isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</td>
                    <td className="text-sm text-secondary" style={{ whiteSpace: 'nowrap' }}>{formatFecha(s.creado)}</td>
                    <td style={{ fontWeight: 600 }}>{s.nombre} {s.apellido}</td>
                    <td className="text-sm">
                      <div>{s.email}</div>
                      <div className="text-secondary">{s.whatsapp}</div>
                    </td>
                    <td className="text-sm">{s.items?.length || 0} artículo{s.items?.length === 1 ? '' : 's'}</td>
                    <td className="text-sm mono">{s.presupuesto_estimado ? `$${Number(s.presupuesto_estimado).toFixed(2)}` : '—'}</td>
                    <td className="text-sm">{FORMA_PAGO_LABEL[s.forma_pago_preferida] || s.forma_pago_preferida}</td>
                    <td className="text-sm">
                      {s.cliente_detalle ? (
                        <span className="mono">{s.cliente_detalle.codigo_casillero}</span>
                      ) : (
                        <span className="text-secondary">Sin vincular</span>
                      )}
                    </td>
                    <td className="text-sm mono" style={{ fontWeight: 600 }}>
                      {s.total_pagar_ahora ? `$${Number(s.total_pagar_ahora).toFixed(2)}` : '—'}
                      {s.pagado && <span title="Pagado" style={{ color: '#2E7D32', marginLeft: '4px' }}>✓</span>}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <select
                        value={s.estado}
                        onChange={(e) => cambiarEstado(s.id, e.target.value)}
                        style={{
                          background: ec.bg, color: ec.color, border: 'none', borderRadius: '6px',
                          padding: '4px 8px', fontWeight: 600, fontSize: '12px', cursor: 'pointer', minWidth: '150px',
                        }}
                      >
                        {Object.entries(ESTADO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={11} style={{ padding: 'var(--spacing-4)', background: 'var(--color-bg-secondary)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--spacing-4)' }}>
                          <div>
                            <h4 style={{ marginBottom: 'var(--spacing-3)', fontSize: '13px' }}>Cotización — precio y peso por artículo</h4>
                            {(s.items || []).length === 0 ? (
                              <p className="text-sm text-secondary">Sin artículos.</p>
                            ) : (
                              <div style={{ display: 'grid', gap: '8px' }}>
                                {s.items.map((it) => (
                                  <div key={it.id} className="card" style={{ padding: '10px 12px' }}>
                                    <strong className="text-sm">{it.descripcion}</strong>
                                    {/* Todo lo que el cliente puso en el formulario, siempre visible y
                                        etiquetado — así no hay que adivinar ni volver a preguntarle,
                                        sobre todo con el link (a veces es del carrito, no del producto). */}
                                    <div style={{
                                      display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px',
                                      fontSize: '12px', margin: '6px 0', color: 'var(--color-text-secondary)',
                                    }}>
                                      <span>Cantidad:</span><span style={{ color: 'var(--color-text)' }}>{it.cantidad}</span>
                                      <span>Talla/color/variante:</span>
                                      <span style={{ color: 'var(--color-text)' }}>{it.variante || '—'}</span>
                                      <span>Link:</span>
                                      <span>
                                        {it.link ? (
                                          <a href={it.link} target="_blank" rel="noopener noreferrer"
                                            style={{ color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', gap: '4px', wordBreak: 'break-all' }}>
                                            <Link2 size={12} /> {it.link} <ExternalLink size={11} />
                                          </a>
                                        ) : '—'}
                                      </span>
                                    </div>
                                    <div className="form-row" style={{ marginTop: '6px', borderTop: '1px solid var(--color-border)', paddingTop: '8px' }}>
                                      <input
                                        type="number" step="0.01" min="0" placeholder="Precio tienda (c/u)"
                                        value={preciosTemp[it.id]?.precio_unitario_tienda ?? ''}
                                        onChange={(e) => actualizarPrecioTemp(it.id, 'precio_unitario_tienda', e.target.value)}
                                        style={{ fontSize: '12px' }}
                                      />
                                      <input
                                        type="number" step="0.01" min="0" placeholder="Peso lb (c/u)"
                                        value={preciosTemp[it.id]?.peso_lb_estimado ?? ''}
                                        onChange={(e) => actualizarPrecioTemp(it.id, 'peso_lb_estimado', e.target.value)}
                                        style={{ fontSize: '12px' }}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            <button className="btn-primary btn-sm" style={{ marginTop: 'var(--spacing-2)' }}
                              onClick={() => cotizar(s)} disabled={cotizando === s.id}>
                              <Calculator size={14} /> {cotizando === s.id ? 'Calculando...' : 'Calcular cotización'}
                            </button>

                            {cotizando === s.id ? (
                              <p className="text-sm text-secondary" style={{ marginTop: 'var(--spacing-3)' }}>Calculando...</p>
                            ) : s.total_pagar_ahora && (
                              <div className="card" style={{ padding: 'var(--spacing-3)', marginTop: 'var(--spacing-3)' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '12px' }}>
                                  <span className="text-secondary">Valor productos:</span><span className="mono text-right">${Number(s.valor_productos).toFixed(2)}</span>
                                  <span className="text-secondary">Comisión compra asistida:</span><span className="mono text-right">${Number(s.comision).toFixed(2)}</span>
                                  <span style={{ fontWeight: 700 }}>A pagar ahora:</span><span className="mono text-right" style={{ fontWeight: 700 }}>${Number(s.total_pagar_ahora).toFixed(2)}</span>
                                  {s.pagado && (
                                    <span style={{ gridColumn: '1 / -1', color: '#2E7D32', fontWeight: 600, marginTop: '2px' }}>
                                      ✓ Pagado{s.payphone_tarjeta ? ` con ${s.payphone_tarjeta}` : ''}{s.fecha_pago ? ` · ${formatFecha(s.fecha_pago)}` : ''}
                                    </span>
                                  )}
                                  {/* A propósito NO se muestra un "total estimado final" combinado acá —
                                      sumar "a pagar ahora" con el estimado de envío confundía, parecía
                                      que al cliente le faltaba pagar eso. Es solo informativo. */}
                                  <span className="text-secondary" style={{ gridColumn: '1 / -1', marginTop: '4px', borderTop: '1px solid var(--color-border)', paddingTop: '4px' }}>Informativo — se factura después, al llegar el paquete:</span>
                                  <span className="text-secondary">Envío + aduana (est.):</span><span className="mono text-right text-secondary">${Number(s.envio_estimado).toFixed(2)}</span>
                                </div>
                                <button className="btn-secondary btn-sm" style={{ marginTop: 'var(--spacing-3)', width: '100%' }}
                                  onClick={() => setMostrarImagen(s.id)}>
                                  <ImageIcon size={14} /> Generar imagen para enviar
                                </button>
                              </div>
                            )}
                          </div>

                          <div>
                            <h4 style={{ marginBottom: 'var(--spacing-3)', fontSize: '13px' }}>Cliente</h4>
                            {s.cliente_detalle ? (
                              <p className="text-sm">
                                <span className="mono">{s.cliente_detalle.codigo_casillero}</span> — {s.cliente_detalle.nombre}
                              </p>
                            ) : vinculando === s.id ? (
                              <SeleccionarClienteInput seleccionado={null} onSelect={(c) => c && vincularCliente(s.id, c)} label="" />
                            ) : (
                              <button className="btn-secondary btn-sm" onClick={() => setVinculando(s.id)}>Vincular a un cliente existente</button>
                            )}

                            <h4 style={{ margin: 'var(--spacing-3) 0', fontSize: '13px' }}>Paquete{(s.paquetes_generados || []).length > 1 ? 's' : ''}</h4>
                            {(s.paquetes_generados || []).length > 0 && (
                              <div style={{ display: 'grid', gap: '4px', marginBottom: 'var(--spacing-2)' }}>
                                {s.paquetes_generados.map((p) => (
                                  <div key={p.id} className="text-sm">
                                    <span className="mono">{p.tracking}</span>
                                    {' — '}
                                    <span style={{ color: 'var(--color-text-secondary)' }}>
                                      {p.estado_actual_detalle?.nombre || '—'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {s.estado === 'confirmada' ? (
                              <button className="btn-secondary btn-sm" onClick={() => setCreandoPaquete(s.id)}>
                                <Package size={14} /> {(s.paquetes_generados || []).length > 0 ? 'Agregar otro paquete' : 'Crear paquete'}
                              </button>
                            ) : (s.paquetes_generados || []).length === 0 && (
                              <p className="text-sm text-secondary">Se habilita al confirmar la solicitud.</p>
                            )}

                            <h4 style={{ margin: 'var(--spacing-3) 0', fontSize: '13px' }}>Notas internas</h4>
                            <textarea
                              rows={3} style={{ width: '100%' }}
                              value={notasTemp[s.id] ?? ''}
                              onChange={(e) => setNotasTemp((prev) => ({ ...prev, [s.id]: e.target.value }))}
                            />
                            <button className="btn-primary btn-sm" style={{ marginTop: 'var(--spacing-2)' }} onClick={() => guardarNotas(s.id)}>
                              Guardar notas
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {solicitudes.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 'var(--spacing-4)', gap: 'var(--spacing-2)' }}>
          <span className="text-sm text-secondary">
            {(paginaActual - 1) * porPagina + 1}–{Math.min(paginaActual * porPagina, solicitudes.length)} de {solicitudes.length}
          </span>
          <button className="btn-ghost btn-sm" disabled={paginaActual <= 1} onClick={() => setPagina(paginaActual - 1)}>
            <ChevronLeft size={16} />
          </button>
          <button className="btn-ghost btn-sm" disabled={paginaActual >= totalPaginas} onClick={() => setPagina(paginaActual + 1)}>
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {mostrarImagen && (
        <CotizacionCompraAsistidaImagenModal
          solicitud={solicitudes.find((s) => s.id === mostrarImagen)}
          onClose={() => setMostrarImagen(null)}
          onEnviada={(actualizada) => setSolicitudes((prev) => prev.map((x) => (x.id === actualizada.id ? actualizada : x)))}
        />
      )}

      {creandoPaquete && (() => {
        const s = solicitudes.find((x) => x.id === creandoPaquete);
        if (!s) return null;
        const descripcion = (s.items || [])
          .map((it) => `${it.descripcion}${it.cantidad > 1 ? ` x${it.cantidad}` : ''}${it.variante ? ` (${it.variante})` : ''}`)
          .join(', ');
        return (
          <PaqueteFormModal
            prellenado={{
              cliente: s.cliente_detalle,
              origen: 'compra-asistida',
              solicitudId: s.id,
              etiqueta: 'Compra Asistida',
              datos: { descripcion },
            }}
            onClose={() => setCreandoPaquete(null)}
            onGuardado={(solicitudActualizada) => {
              setSolicitudes((prev) => prev.map((x) => (x.id === solicitudActualizada.id ? solicitudActualizada : x)));
              setCreandoPaquete(null);
            }}
          />
        );
      })()}
    </div>
  );
}
